import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { attributeTypes } from '@nsr/react-query-builder-executor';
import { spacing20, spacing30, spacing40, spacing60, colorTextNeutral200 } from '@ellucian/react-design-system/core/styles/tokens';
import { CONSTANTS } from '../../Helpers/Configuration';
import { populateAttributesAfterSelection, fetchEedmValues } from '@nsr/react-query-builder-api';
import { useApi } from '../../ApiContext';
import { useSnackbar } from '../../SnackbarContext/SnackbarContext';
import { buildQueryJson, buildModelAttributeForSorting, buildModelAttributeObject, mapAttributesToResults } from '../../Helpers/QueryHelpers';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { IMAGES, makeStyles, useMediaQuery, Dropdown, DropdownItem, Search, Typography, IconButton, SimpleDialog, Divider, DatePicker, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, TableFooter, Pagination, Radio, Illustration, Button } from '@ellucian/react-design-system/core';
import { getRecordNotFoundAttributeForTable } from '../../Helpers/ApplicationFilteredSearchHelpers';
import useApplicationForm from '../useApplicationForm';
import get from 'lodash/get';
import { Collapse } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';

const useStyles = makeStyles({
    name: 'ApplicationFilteredSearch'
})(() => ({
    paper: {
        height: '100dvh',
        maxHeight: '-webkit-fill-available',
        maxWidth: '100vw',
        width: ['100vw', '!important'],
        padding: `${spacing40} !important`
    },
    helperText: {
        paddingTop: spacing20,
    },
    fieldsContainer: {
        display: 'grid',
        alignItems: 'center',
        gridColumnGap: spacing60,
        gridTemplateColumns: 'auto auto',
    },
    fieldContainer: {
        paddingBottom: spacing60,
    },
    divider: {
        marginBottom: [spacing60, '!important'],
    },
    dropdownItemRoot: {
        height: 'auto !important',
    },
    noRecordsFound: {
        display: 'grid',
        textAlign: 'center',
    },
    radioCell: {
        width: '0.5rem'
    },
    radioCellMobile: {
        marginLeft: ['-0.5rem', '!important'],
        paddingLeft: spacing20,
    },
    recordTableRow: {
        cursor: ['pointer', '!important'],
        paddingLeft: spacing30,
    },
    tableCellActions: {
        display: ['inline-flex', '!important']
    },
    tableCellMobileView: {
        '&::before': { width: 'auto !important'}
    },
    deleteIconButton: {
        marginLeft: spacing20
    },
    searchButtonIcon: {
        paddingRight: spacing20
    },
    searchButton: {
        paddingTop: spacing30
    },
    tableExpandableRow: {
        padding: ['0px', '!important'],
        margin: ['0px', '!important'],
    },
    tableContainer: {
        background: colorTextNeutral200,
        padding: spacing30
    },
    tableCellNoBorder: {
        border: 'none',
        '&::before': { width: 'auto !important'}
    },
    filterHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        padding: `${spacing30} 0`,
        '&:hover': {
            opacity: 0.8,
        },
    },
    filterChevron: {
        transition: 'transform 0.3s ease',
    },
    filterChevronOpen: {
        transform: 'rotate(180deg)',
    },
    filterDropShadow: {
        paddingBottom: spacing30,
        position: 'relative',
        '&::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '16px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.08), transparent)',
            pointerEvents: 'none',
        },
        marginBottom: spacing40,
    },
}));

const defaultEndYear = () => moment().add(50, 'years').year();
const defaultStartYear = () => moment().subtract(20, 'years').year();

const ApplicationFilteredSearch = props => {
    const { adminTenant, adminApi } = useApplicationForm();
    const { supportedGraphqlModels } = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const { classes } = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { formatMessage } = useIntl();
    const contextApi = useApi();
    const api = adminApi || contextApi;
    const snackbar = useSnackbar();
    const {
        filteredSearch: filteredSearchProp,
        currentValue = null,
        fullWidth = true,
        label,
        onAdd,
        onBlur,
        codeFieldInfo
    } = props;

    const defaultSortAndPageOption = useMemo(() => {
        let orderBy = '';
        let activeRowId = '';
        if (filteredSearchProp?.displayAttributes) {
            orderBy = buildModelAttributeForSorting(
                filteredSearchProp.displayAttributes[0]?.value,
                filteredSearchProp.displayAttributes[0]?.path,
                filteredSearchProp.displayAttributes[0]?.type
            );
            activeRowId = filteredSearchProp.displayAttributes[0]?.id;
        }
        return (
            {
                order: 'asc',
                page: 0,
                rowsPerPage: 10,
                orderBy,
                activeRowId
            }
        );
    });
    const buttons = useMemo(() => [
        { id: 'add', title: formatMessage({ id: 'filteredSearchDialogAddButton' }) },
        { id: 'cancel', title: formatMessage({ id: 'applicantHomeCancel' }) },
    ]);
    const [filterRecordsResults, setFilterRecordsResults] = useState({ list: [], totalCount: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [filteredSearch, setFilteredSearch] = useState(cloneDeep(filteredSearchProp ?? {}));
    const noRecordsFoundRecord = useMemo(() => {
        if (filteredSearchProp?.recordNotFoundAttribute) {
            return getRecordNotFoundAttributeForTable(filteredSearchProp.recordNotFoundAttribute, formatMessage, codeFieldInfo);
        }
        return { id: '', label: '' };
    }, [filteredSearchProp?.recordNotFoundAttribute, codeFieldInfo]);
    const [filteredSearchDialogOpen, setFilteredSearchDialogOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);
    const [sortAndPageOptions, setSortAndPageOptions] = useState({ ...defaultSortAndPageOption });
    const [, setUpdate] = useState(false);
    const [timeOutId, setTimeOutId] = useState(null);

    const handleButtonClick = useCallback(async (buttonId) => {
        switch (buttonId) {
            case 'cancel':
                setFilteredSearchDialogOpen(false);
                break;
            case 'add':
                onAdd && onAdd(selectedRecord ?? null);
                setFilteredSearchDialogOpen(false);
                break;
        }
        resetFilterValues();
    }, [selectedRecord]);

    const deleteRecord = useCallback(() => {
        onAdd && onAdd(null);
        resetFilterValues();
    }, []);

    const resetFilterValues = () => {
        setSelectedRecord(null);
        setSortAndPageOptions({ ...defaultSortAndPageOption });
        setFilteredSearch(cloneDeep(filteredSearchProp ?? {}));
    };

    useEffect(() => {
        filteredSearchDialogOpen && filterRecords();
    }, [filteredSearchDialogOpen]);

    const onChange = (field, event) => {
        switch (field.type) {
            case attributeTypes.string:
                field.filterText = event.target.value;
                break;
            case attributeTypes.enum:
            case attributeTypes.lookup:
                if (field?.loading) {
                    return;
                }
                if (event.target.value === 'None') {
                    onClear(field);
                    return;
                }
                field.filterText = event.target.value;
                break;
            case attributeTypes.date:
                field.filterText = event;
                break;
            default:
                break;
        }
        setUpdate(x => !x);
        debounceFilterSearch();
    };

    // dont fetch on every change, wait for a second
    const debounceFilterSearch = () => {
        const id = setTimeout(async () => {
            await filterRecords(true);
            setTimeOutId(null);
        }, 1000);
        timeOutId && clearTimeout(timeOutId);
        setTimeOutId(id);
    };

    const onClear = async (field) => {
        field.filterText = '';
        setUpdate(x => !x);
        await filterRecords(true);
    };

    const onOpenDropdown = async (field, type) => {
        field.loading = true;
        setUpdate(x => !x);
        switch (type) {
            case attributeTypes.enum:
                await populateAttributeDropDownValues(field);
                break;
            case attributeTypes.lookup:
                await fetchLookupValues(field);
                break;
            default:
                break;
        }
        field.loading = false;
        setUpdate(x => !x);
    };

    const onClickSearchButton = () => {
        setFilteredSearchDialogOpen(true);
        onBlur && onBlur();
    };

    const localOnBlur = (e) => {
        onBlur && onBlur(e);
    };

    const handleChangePage = useCallback(async (_, page) => {
        sortAndPageOptions.page = page;
        setSortAndPageOptions({ ...sortAndPageOptions });
        await filterRecords();
    }, [filterRecords, filterRecordsResults]);

    const handleChangeRowsPerPage = useCallback(async (event) => {
        sortAndPageOptions.rowsPerPage = event.target.value;
        setSortAndPageOptions({ ...sortAndPageOptions });
        await filterRecords();
    }, [filterRecords, filterRecordsResults]);

    const selectRecord = (record) => {
        setSelectedRecord({ ...record });
    };

    const getQuery = () => {
        const attributes = filteredSearch?.attributes;
        const filterQuery = filteredSearch?.filterQuery;
        let query = { groups: [], where: [] };
        if (filterQuery && filterQuery?.where?.length > 0 && filterQuery?.groups?.length > 0) {
            // we dont want to change filteredSearch.filterQuery so clone it
            query = cloneDeep(filterQuery);
            // build the query for the applicant filter fields 
            const buildQueryJsonForAttributes = buildQueryJson(attributes);
            const queryJsonForAttributesGroupId = buildQueryJsonForAttributes?.groups[0]?.id;
            // add the filter field query groupId as a top level AND
            query.where.forEach((att) => att.groupIds.unshift(queryJsonForAttributesGroupId));
            // combine the two filters together 
            query.where = query.where.concat(buildQueryJsonForAttributes?.where || []);
            query.groups = query.groups.concat(buildQueryJsonForAttributes?.groups || []);
        } else {
            query = buildQueryJson(attributes);
        }
        return query;
    };

    // *** Fetch filter results ***//
    const filterRecords = async (resetSortAndPageOptions = false) => {
        setIsLoading(true);
        setSelectedRecord(null);
        const codeField = codeFieldInfo?.codeField;
        resetSortAndPageOptions && setSortAndPageOptions({ ...defaultSortAndPageOption });
        const model = filteredSearch?.model;
        const displayAttributes = filteredSearch?.displayAttributes;
        const queryAttributes = buildModelAttributeObject(displayAttributes);

        if (codeField && !queryAttributes.includes(codeField)) {
            queryAttributes.push(codeField);
        }

        const query = getQuery();
        try {
            let results = [];
            results = await fetchEedmValues(
                api.dataAccess.graphApi || (await api.dataAccess.getGraphApi()),
                supportedGraphqlModels[model].graphType,
                query,
                queryAttributes,
                sortAndPageOptions,
                '',
                '',
                true,
                true
            );
            results.list = mapAttributesToResults(displayAttributes, results?.list, codeFieldInfo?.codeFieldPath);
            const isLastPage = (sortAndPageOptions.page + 1) * sortAndPageOptions.rowsPerPage >= results.totalCount;
            if (isLastPage) {
                results?.list?.push(noRecordsFoundRecord);
            }
            setFilterRecordsResults(results);
        } catch (err) {
            const id = 'generalErrorMessage';
            snackbar.show(formatMessage({ id }));
            setFilterRecordsResults({ list: [], totalCount: 0 });
            console.error(err);
        }
        setIsLoading(false);
    };
    const populateAttributeDropDownValues = async (field) => {
        try {
            const model = filteredSearch.model;
            await populateAttributesAfterSelection(api.dataAccess.graphApi || (await api.dataAccess.getGraphApi()), supportedGraphqlModels[model].graphType, [{ attribute: field }]);

        } catch (err) {
            field.enum = [];
            const id = 'generalErrorMessage';
            snackbar.show(formatMessage({ id }));
            console.error(err);
        }
    };

    const fetchLookupValues = async (field) => {
        try {
            const attributes = await api.dataAccess.getLookupValues(field.graphQLTypeName, '');
            field.enum = attributes?.list?.map((att) => {
                return {
                    value: att.id,
                    label: att['title'],
                    code: get(att, codeFieldInfo?.codeFieldPath)
                };
            });
        } catch (err) {
            const id = 'generalErrorMessage';
            snackbar.show(formatMessage({ id }));
            console.error(err);
        }
    };

    const getValueFromRecord = (record, field) => {
        if (!field.path || field.path.length === 0) {
            return record[field.value] || record.label || '';
        }
        const pathValues = field.path.map(p => p.value);
        const keyName = `${pathValues.join('.')}.${field.value}`;
        return record[keyName] || record.label || '';
    };

    const buildFilteredSearchApplicationTableValue = () => {
        const recordValue = currentValue;
        const displayFields = filteredSearch?.displayAttributes ?? [];
        if (!recordValue || !displayFields) {
            return null;
        }
        const noRecordFoundColSpan = (Object.keys(recordValue)?.length - 1) !== displayFields?.length ? displayFields.length : 1;
        const tableHeadsLabels = displayFields?.map((field) => formatMessage({ id: field.displayLabelId }));
        return (
            <Table
                layout={{
                    variant: 'card',
                    breakpoint: isMobile ? 'lg' : 'md',
                }}
            >
                <TableHead>
                    <TableRow>
                        {tableHeadsLabels?.map((label, labelIndex) => {
                            return (
                                <TableCell align="left" key={`TableHead_TableCell_${labelIndex}`}>
                                    {label}
                                </TableCell>
                            );
                        })}
                        <TableCell align="left" >
                            {formatMessage({ id: 'filteredSearchDialogActionsRow' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow className={classes.tableExpandableRow}>
                        {Object.keys(recordValue)?.slice(1)?.length === 1 && recordValue.label && (// Record not found
                            (<TableCell
                                colSpan={noRecordFoundColSpan}
                                align={'left'}
                                columnName={tableHeadsLabels[0]}
                                className={classes.tableCellNoBorder}
                            >
                                {recordValue.label || ''}
                            </TableCell>)
                        )}
                        {(Object.keys(recordValue)?.slice(1)?.length > 1 || !recordValue.label) &&
                            displayFields?.map((field, fieldIndex) => {
                                return (
                                    <TableCell
                                        key={`TableBody_TableCell_${field.value}_${fieldIndex}`}
                                        align={'left'}
                                        columnName={tableHeadsLabels[fieldIndex]}
                                        className={classes.tableCellNoBorder}
                                    >
                                        {getValueFromRecord(recordValue, field)}
                                    </TableCell>
                                );
                            })}
                        <TableCell className={classes.tableCellNoBorder} align="left" columnName={formatMessage({ id: 'filteredSearchDialogActionsRow' })}>
                            <div className={classes.tableCellActions}>
                                {buildFilteredSearchApplicationIconButton('trash', 'filteredSearchDeleteIcon', deleteRecord, classes.deleteIconButton)}
                            </div>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        );
    };

    const buildFilteredSearchApplicationIconButton = (iconName, formatMessageId, onClick, className) => {
        return (
            <div>
                <IconButton
                    className={className}
                    color="secondary"
                    title={formatMessage({ id: formatMessageId })}
                    id={'SecondaryFilterSearchIconButton'}
                    aria-label={formatMessage({ id: formatMessageId })}
                    onClick={onClick}
                >
                    <Icon name={iconName} />
                </IconButton>
            </div>
        );
    };

    const filterFields = filteredSearch?.attributes ?? [];
    const displayFields = filteredSearch?.displayAttributes ?? [];
    const noRecordFoundColSpan = filterFields?.length ?? 1;
    const records = filterRecordsResults?.list ?? [];
    const recordsTotalCount = filterRecordsResults?.totalCount ?? 0;
    return (
        <>
            {(!currentValue || !currentValue?.id) && !filteredSearchDialogOpen &&
                <div className={classes.searchButton}>
                    <Button
                        onClick={onClickSearchButton}
                        color='secondary'
                        onBlur={localOnBlur}
                    >
                        <Icon name="search" className={classes.searchButtonIcon} />
                        {formatMessage({ id: 'filteredSearchSelectRecordIcon' })}
                    </Button>
                </div>
            }
            {currentValue && currentValue?.id && !filteredSearchDialogOpen &&
                <div className={classes.tableContainer}>
                    {buildFilteredSearchApplicationTableValue()}
                </div>

            }
            {filteredSearchDialogOpen &&
                <SimpleDialog
                    PaperProps={{ className: classes.paper }}
                    title={label}
                    open={filteredSearchDialogOpen}
                    closeOnClickOut={true}
                    showTitleBarClose={true}
                    maxWidth="md"
                    onClose={() => handleButtonClick('cancel')}
                    fullWidth
                    fullScreen={isMobile}
                    handleButtonClick={handleButtonClick}
                    buttonConfig={buttons}
                >
                    <Divider className={classes.divider} />
                    {isMobile ? (
                        <>
                            <div
                                className={classes.filterHeader}
                                onClick={() => setFiltersExpanded(!filtersExpanded)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && setFiltersExpanded(!filtersExpanded)}
                            >
                                <Typography variant="body1">
                                    {formatMessage({ id: 'filteredSearchDialogInfoText' })}
                                </Typography>
                                <Icon
                                    name="chevron-down"
                                    className={`${classes.filterChevron} ${filtersExpanded ? classes.filterChevronOpen : ''}`}
                                />
                            </div>
                            <Collapse in={filtersExpanded} timeout={300}>
                                <div className={classes.filterDropShadow}>
                                    {filterFields?.map((field, i) => {
                                        return (
                                            <div key={i} className={classes.fieldContainer}>
                                                {field.type === attributeTypes.string &&
                                                    <Search
                                                        fullWidth={fullWidth}
                                                        label={formatMessage({ id: field.displayLabelId })}
                                                        onChange={(event) => onChange(field, event)}
                                                        onClear={() => onClear(field)}
                                                        value={field.filterText}
                                                    >
                                                    </Search>
                                                }
                                                {(field.type === attributeTypes.enum || field.type === attributeTypes.lookup) &&
                                                    <Dropdown
                                                        fullWidth={fullWidth}
                                                        label={formatMessage({ id: field.displayLabelId })}
                                                        onOpen={(field.loading || field.enum) ? null : () => onOpenDropdown(field, field.type)}
                                                        onChange={(event) => onChange(field, event)}
                                                        value={field.filterText ?? null}
                                                    >
                                                        {!field.loading &&
                                                            <DropdownItem
                                                                label={formatMessage({ id: 'filteredSearchDropDownNoneValue' })}
                                                                value="None"
                                                            />
                                                        }
                                                        {!field.loading && field?.enum?.map((value, i) => {
                                                            return (
                                                                <DropdownItem
                                                                    key={`${value.value}_${i}`}
                                                                    label={value.label}
                                                                    value={value.value}
                                                                    classes={{ root: classes.dropdownItemRoot }}
                                                                />
                                                            );
                                                        })}
                                                        {field.loading &&
                                                            < DropdownItem
                                                                label={formatMessage({ id: 'loading' })}
                                                                value={'loading'}
                                                                classes={{ root: classes.dropdownItemRoot }}
                                                                LeftIconComponent={<CircularProgress size={20} />}
                                                            />
                                                        }
                                                    </Dropdown>
                                                }
                                                {field.type === attributeTypes.date &&
                                                    <DatePicker
                                                        fullWidth={fullWidth}
                                                        label={formatMessage({ id: field.displayLabelId })}
                                                        onChange={(event) => onChange(field, event)}
                                                        endYear={defaultEndYear()}
                                                        startYear={defaultStartYear()}
                                                        format={CONSTANTS.defaultDateFormat}
                                                        value={field.filterText ?? null}
                                                    >
                                                    </DatePicker>
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            </Collapse>
                        </>
                    ) : (
                        <>
                            <Typography
                                paragraph
                                variant={'body1'}
                            >
                                {formatMessage({ id: 'filteredSearchDialogInfoText' })}
                            </Typography>
                            <div className={classes.fieldsContainer}>
                                {filterFields?.map((field, i) => {
                                    return (
                                        <div key={i} className={classes.fieldContainer}>
                                            {field.type === attributeTypes.string &&
                                                <Search
                                                    fullWidth={fullWidth}
                                                    label={formatMessage({ id: field.displayLabelId })}
                                                    onChange={(event) => onChange(field, event)}
                                                    onClear={() => onClear(field)}
                                                    value={field.filterText}
                                                >
                                                </Search>
                                            }
                                            {(field.type === attributeTypes.enum || field.type === attributeTypes.lookup) &&
                                                <Dropdown
                                                    fullWidth={fullWidth}
                                                    label={formatMessage({ id: field.displayLabelId })}
                                                    onOpen={(field.loading || field.enum) ? null : () => onOpenDropdown(field, field.type)}
                                                    onChange={(event) => onChange(field, event)}
                                                    value={field.filterText ?? null}
                                                >
                                                    {!field.loading &&
                                                        <DropdownItem
                                                            label={formatMessage({ id: 'filteredSearchDropDownNoneValue' })}
                                                            value="None"
                                                        />
                                                    }
                                                    {!field.loading && field?.enum?.map((value, i) => {
                                                        return (
                                                            <DropdownItem
                                                                key={`${value.value}_${i}`}
                                                                label={value.label}
                                                                value={value.value}
                                                                classes={{ root: classes.dropdownItemRoot }}
                                                            />
                                                        );
                                                    })}
                                                    {field.loading &&
                                                        < DropdownItem
                                                            label={formatMessage({ id: 'loading' })}
                                                            value={'loading'}
                                                            classes={{ root: classes.dropdownItemRoot }}
                                                            LeftIconComponent={<CircularProgress size={20} />}
                                                        />
                                                    }
                                                </Dropdown>
                                            }
                                            {field.type === attributeTypes.date &&
                                                <DatePicker
                                                    fullWidth={fullWidth}
                                                    label={formatMessage({ id: field.displayLabelId })}
                                                    onChange={(event) => onChange(field, event)}
                                                    endYear={defaultEndYear()}
                                                    startYear={defaultStartYear()}
                                                    format={CONSTANTS.defaultDateFormat}
                                                    value={field.filterText ?? null}
                                                >
                                                </DatePicker>
                                            }
                                        </div>
                                    );
                                })
                                }
                            </div>
                        </>
                    )}
                    <Divider />
                    {isLoading &&
                        <div className={classes.noRecordsFound}>
                            <CircularProgress />
                        </div>
                    }
                    {!isLoading && (!records || records?.length === 0) &&
                        <div className={classes.noRecordsFound}>
                            <Illustration name={IMAGES.NO_SEARCH_RESULTS} />
                            <Typography variant="h3"><FormattedMessage id="filteredSearchNoRecordsFound" /></Typography>
                        </div>
                    }
                    {!isLoading && records?.length > 0 &&
                        <Table
                            layout={{
                                variant: 'card',
                                breakpoint: isMobile ? 'lg' : 'sm',
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell size="small" padding="none" className={!isMobile ? classes.radioCell : classes.radioCellMobile}></TableCell>
                                    {displayFields?.map((field, fieldIndex) => {
                                        return (
                                            <TableCell align="left" key={`TableHead_TableCell_${fieldIndex}`}>
                                                {formatMessage({ id: field.displayLabelId })}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records?.map((record, recordIndex) => {
                                    const isNoRecordFoundRow = record?.id === noRecordsFoundRecord?.id;
                                    return (
                                        <TableRow
                                            key={`TableBody_TableRow_${recordIndex}`}
                                            hover={true}
                                            selected={selectedRecord?.id === record.id}
                                            onClick={() => selectRecord(record?.id === noRecordsFoundRecord?.id ? noRecordsFoundRecord : record)}
                                            className={classes.recordTableRow}
                                        >
                                            <TableCell size="small" padding="none" className={!isMobile ? classes.radioCell : classes.radioCellMobile}>
                                                <Radio
                                                    onChange={() => selectRecord(record?.id === noRecordsFoundRecord?.id ? noRecordsFoundRecord : record)}
                                                    checked={selectedRecord?.id === record.id}
                                                ></Radio>
                                            </TableCell>
                                            {isNoRecordFoundRow &&
                                                <TableCell
                                                    key={`TableBody_TableCell_id_${recordIndex}`}
                                                    colSpan={noRecordFoundColSpan}
                                                    align="left"
                                                    columnName={formatMessage({ id: displayFields[0]?.displayLabelId })}
                                                >
                                                    {record.label}
                                                </TableCell>
                                            }
                                            {!isNoRecordFoundRow && displayFields?.map((field, fieldIndex) => {
                                                return (
                                                    <TableCell
                                                        className = {classes.tableCellMobileView}
                                                        key={`TableBody_TableCell_${fieldIndex}_${recordIndex}`}
                                                        colSpan="1"
                                                        align="left"
                                                        columnName={formatMessage({ id: field.displayLabelId })}
                                                    >
                                                        {getValueFromRecord(record, field)}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <Pagination
                                        component="td"
                                        count={recordsTotalCount}
                                        rowsPerPage={sortAndPageOptions.rowsPerPage}
                                        page={sortAndPageOptions.page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    }
                </SimpleDialog>
            }
        </>
    );
};

export default ApplicationFilteredSearch;